// src/services/auth.jsx
import { isTokenExpired } from "@/utils/authToken"

/**
 * Return a usable access token.
 * - If the current token is expired (NO skew), refresh it.
 * - Otherwise return the current token.
 * @param {{ token?: string, environment?: { auth?: { url?: string } } }} accessContext
 * @param {(updater: (prev:any)=>any)=>void} [setAccessContext]
 * @returns {Promise<string|null>} new/current token, or null on failure
 */
export async function checkAndRefreshToken(accessContext, setAccessContext) {
  const token = accessContext?.token ?? null
  const base = accessContext?.environment?.auth?.url

  if (token && !isTokenExpired(token, 60)) {
    return token
  }

  if (!base) {
    console.error("auth: Missing auth base URL in accessContext.environment.auth.url")
    return null
  }

  try {
    const res = await fetch(`${base}/auth/token`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    })

    if (!res.ok) {
      console.error("auth: token refresh failed:", res.status, res.statusText)
      return null
    }

    let data = null
    try {
      data = await res.json()
    } catch {
      data = null
    }

    const newToken = data?.accessToken ?? null
    if (!newToken) {
      console.error("auth: refresh response missing accessToken")
      return null
    }

    if (typeof setAccessContext === "function") {
      setAccessContext((prev) => ({ ...prev, token: newToken }))
    }
    return newToken
  } catch (e) {
    console.error("auth: network or parsing error during token refresh:", e)
    return null
  }
}

/**
 * Merge an Authorization: Bearer header into fetch options.
 * @param {RequestInit} opts
 * @param {string} token
 * @returns {RequestInit}
 */
export function withBearer(opts, token) {
  return {
    ...opts,
    headers: { ...(opts?.headers || {}), Authorization: `Bearer ${token}` },
  }
}

export default {
  checkAndRefreshToken,
  withBearer,
}
